/**
 * User Types
 * Basic user profile and creation payloads.
 */
/**
 * Human-readable labels for role chips and selectors.
 *
 * This map is the single source of truth for "what roles exist". Derive
 * selector options from `Object.keys(USER_ROLE_LABELS)` rather than writing a
 * second array of role strings — a duplicated list is typed `UserRole[]`, so
 * omitting a role from it compiles cleanly and the role silently never appears
 * in the UI.
 */
export const USER_ROLE_LABELS = {
    admin: 'Admin',
    pm: 'PM',
    engineer: 'Engineer',
    techWriter: 'Tech Writer',
    marketing: 'Marketing',
    viewer: 'Viewer',
    factory: 'Factory',
    dev: 'Dev',
    priceListViewer: 'Price List Viewer',
    priceListEditor: 'Price List Editor',
};
/**
 * Internal comments are visible to anyone holding a non-factory role; a
 * factory-only user (external manufacturing partner) is restricted to the
 * external comment channel. Single source of truth for both the backend
 * EntityRegistry resolver and the frontend tab gate.
 */
export function canAccessInternalComments(roles) {
    return isInternalUser(roles);
}
/**
 * True unless the account is a factory-only one — an external manufacturing
 * partner. The single test for "is this person inside the company", used by the
 * internal-comment gate above and by the middleware that keeps external accounts
 * out of file and asset writes.
 *
 * An account with no roles at all is NOT internal: an unprovisioned or stripped
 * account should fail closed, not inherit staff privileges.
 */
export function isInternalUser(roles) {
    return roles.some((r) => r !== 'factory');
}
/**
 * Roles permitted to change a task's Custom Task Type status (the top-left
 * status pill). Evaluators hold the `engineer` role. Single source of truth for
 * the server-side `requireRole` gate on PATCH /bugs/:gid/status and the
 * frontend render gates — keep them fed from this list, never a copy of it.
 *
 * Holding a listed role is necessary but not sufficient: the status write is
 * performed as the requesting user against *their* Asana OAuth token, so a user
 * with no linked Asana account still gets `asana-not-connected` from the API.
 */
export const BUG_STATUS_EDIT_ROLES = ['admin', 'pm', 'engineer'];
export function canEditBugStatus(roles) {
    return roles.some((r) => BUG_STATUS_EDIT_ROLES.includes(r));
}
/**
 * Roles permitted to change a task's Priority enum. Same membership as
 * BUG_STATUS_EDIT_ROLES today, but kept separate so the two gates can diverge
 * without one silently dragging the other along. Carries the same
 * necessary-but-not-sufficient caveat: the write runs against the caller's own
 * Asana OAuth token.
 */
export const BUG_PRIORITY_EDIT_ROLES = ['admin', 'pm', 'engineer'];
export function canEditBugPriority(roles) {
    return roles.some((r) => BUG_PRIORITY_EDIT_ROLES.includes(r));
}
/**
 * Roles permitted to view the PD Dev team dashboard (issue #422).
 *
 * The dashboard surfaces issue titles and bodies from every tracked private
 * repo in the Chauvetlighting org in one place, so this is a real access
 * boundary, not a cosmetic nav gate. `dev` is deliberately narrow — it exists
 * to name a specific group of people and is not implied by `engineer`.
 *
 * `admin` rides along because the tracked-repo picker lives in the admin
 * section; an admin who could configure the dashboard but not see it would be
 * unable to check their own work.
 *
 * Single source of truth for the server-side `requireRole` gate and the
 * frontend nav gate — keep both fed from this list, never a copy of it.
 */
export const DEV_DASHBOARD_ROLES = ['admin', 'dev'];
export function canViewDevDashboard(roles) {
    return roles.some((r) => DEV_DASHBOARD_ROLES.includes(r));
}
/**
 * Roles permitted to check a shipment in as received by PD (issue #455).
 *
 * Deliberately narrow: checking a unit in is a custody record, it closes the
 * shipment out of the active list, and it mails the distribution list plus the
 * products' PMs. `pm` is the group asked for; `admin` rides along as the usual
 * superuser, matching `canPollNow` in shipment.service.ts. `engineer` was added
 * in #1048: the Belgium office's engineers receive the units and could not tick
 * them in.
 *
 * Note this is NOT the same predicate as editing a shipment (`canEdit` there is
 * creator-or-admin). A PM who did not create the shipment must still be able to
 * receive it, which is why check-in has its own action endpoint rather than
 * riding on the generic PATCH.
 *
 * Single source of truth for the service-side gate and the frontend render
 * gate — keep both fed from this list, never a copy of it.
 */
export const SHIPMENT_RECEIVE_ROLES = ['admin', 'pm', 'engineer'];
export function canReceiveShipment(roles) {
    return roles.some((r) => SHIPMENT_RECEIVE_ROLES.includes(r));
}
/**
 * Roles permitted to release an evaluation-process gate (A, B, C), move a
 * point to Future and untick a point's Safety
 * (docs/specs/2026-10-13-evaluation-process-design.md, D1).
 *
 * Holding a listed role is necessary but NOT sufficient, the same shape as the
 * spec review (#1041): the service also requires the caller to be the
 * product's own PM (`metadata.productManager`), or an admin, whose release of
 * someone else's product is recorded as an override. A `pm` who is not this
 * product's PM is refused. See `resolveEvalPmAuthority` in
 * evaluation-process.compute.ts.
 *
 * Single source of truth for the server-side `requireRole` gate and the
 * frontend render gate — keep both fed from this list, never a copy of it.
 */
export const EVAL_GATE_RELEASE_ROLES = ['admin', 'pm'];
export function canReleaseEvalGate(roles) {
    return roles.some((r) => EVAL_GATE_RELEASE_ROLES.includes(r));
}
/**
 * Roles permitted to edit a physical asset's descriptive fields — product link,
 * serial number, warehouse location, category, notes (issue #459).
 *
 * Deliberately wider than the admin/pm gate it replaced. ANY authenticated user
 * can already *register* an asset (scan-and-go is the whole point of the
 * feature) and set every one of these fields at registration; gating the
 * correction of a typo behind an admin was an asymmetry, not a boundary — an
 * evaluator who registered a unit with no product link had no way to add one.
 *
 * `viewer` and `factory` are excluded: read-only staff and external
 * manufacturing partners. Everyone who does hands-on work with units is in.
 * Retire / reactivate / delete stay admin-only — those are lifecycle actions,
 * not descriptive edits.
 *
 * Single source of truth for the server-side `requireRole` gate and the
 * frontend render gate — keep both fed from this list, never a copy of it.
 */
export const PHYSICAL_ASSET_EDIT_ROLES = [
    'admin',
    'pm',
    'engineer',
    'techWriter',
    'marketing',
    'dev',
];
export function canEditPhysicalAsset(roles) {
    return roles.some((r) => PHYSICAL_ASSET_EDIT_ROLES.includes(r));
}
/** Default render order, matching the historical fixed layout. */
export const DEFAULT_PRODUCT_CARD_ORDER = [
    'details',
    'proposal',
    'taxonomy',
    'evaluations',
    'software-evaluations',
    'pdlc',
];
// ─── Role combinations Feature Visibility cannot honour ─────────────────────
/**
 * Roles whose Feature Visibility configuration is a LOCKDOWN: it hides nearly
 * every feature, leaving the one page the role exists for. Grouped, because the
 * roles inside a group are configured with the same hides, so holding two of
 * them changes nothing.
 *
 * Feature Visibility resolves per-role hides with all-must-hide (a hide bites
 * only when EVERY role the user holds names it, see `resolveHiddenFeatures`).
 * That is right for roles that grant: handing someone `admin` on top of `viewer`
 * must not keep them locked out of what `viewer` was denied. It is inverted for
 * these, where the hides ARE the point. Pairing one with a role from outside its
 * group voids the lockdown completely and the account sees all of ATLAS.
 *
 * `factory` is the external manufacturing partner, and `scim.service.ts` already
 * guards the same collapse for internal comments. The price-list pair is Sales;
 * `migrations/20260914-01-seed-price-list-roles.ts` seeds their hides.
 *
 * A role belongs here when its per-role hide list is what keeps it narrow. A
 * role kept narrow by `requireRole` gates in code does not.
 */
export const LOCKDOWN_ROLE_GROUPS = [
    ['factory'],
    ['priceListViewer', 'priceListEditor'],
];
/** The lockdown group one of these roles belongs to, or null for none. */
function lockdownGroupFor(roles) {
    return (LOCKDOWN_ROLE_GROUPS.find((group) => roles.some((role) => group.includes(role))) ?? null);
}
const labelList = (roles) => roles.map((r) => USER_ROLE_LABELS[r] ?? r).join(', ');
/**
 * Why these roles may not be held together, in the words an admin needs, or null
 * when they may.
 *
 * Checked by `POST /users` and `PATCH /users/:userId`, rendered by the Accounts
 * pages, and applied without a human by `resolveRoleConflict` on the SCIM
 * reconcile. One function so the three cannot disagree about what is allowed.
 */
export function roleCombinationError(roles) {
    const group = lockdownGroupFor(roles);
    if (!group)
        return null;
    const others = roles.filter((role) => !group.includes(role));
    if (others.length === 0)
        return null;
    const held = roles.filter((role) => group.includes(role));
    return (`${labelList(held)} restricts this account to the pages that role exists for. ` +
        `Feature Visibility hides a feature only when every role the user holds hides it, ` +
        `so adding ${labelList(others)} unlocks all of ATLAS for them. Keep one or the other.`);
}
/**
 * The roles to keep when a combination cannot be honoured, for the paths where
 * no human is present to choose: group-driven roles from Entra.
 *
 * `admin` survives everything, because an admin dropped to a lockdown role is
 * locked out of the page they would fix it from, and an admin sees everything
 * anyway. Otherwise the lockdown wins and the grants are dropped, which fails
 * closed: the account keeps less than the groups asked for, an admin can see
 * that on the Accounts page, and setting the roles by hand overrides it.
 */
export function resolveRoleConflict(roles) {
    const group = roleCombinationError(roles) ? lockdownGroupFor(roles) : null;
    if (!group)
        return [...roles];
    return roles.includes('admin')
        ? roles.filter((role) => !group.includes(role))
        : roles.filter((role) => group.includes(role));
}
//# sourceMappingURL=user.types.js.map